import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * Bond redemption at maturity.
 *
 * For every holder the desk does not exclude, the run debits the whole bond
 * position (`bond_matured`, shown as "Matured") and credits face value in cash
 * (`bond_maturity_payment`, "Maturity Payment"). Both go to 1C as adjustments.
 *
 * The run is refused unless the bond is clean — no open order, no pending
 * withdrawal / deposit / adjustment, no negative position. The tool never
 * cancels anything; the desk resolves each blocker by hand and previews again.
 *
 * A bond can be redeemed once. Backend: docs/bond-maturity.md.
 */

export type BondMaturityStatus =
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type BondMaturityEntryStatus = 'paid' | 'excluded' | 'skipped' | 'failed';

/** Always branch on the code — messages are prose and not part of the contract. */
export type BondMaturityBlockerCode =
  | 'NOT_A_BOND'
  | 'NO_PRICE_CURRENCY'
  | 'AMBIGUOUS_INSTRUMENT'
  | 'RUN_IN_PROGRESS'
  | 'ALREADY_MATURED'
  | 'NEGATIVE_HOLDERS'
  | 'TOO_MANY_HOLDERS'
  | 'OPEN_STOCK_ORDERS'
  | 'OPEN_OTC_ORDERS'
  | 'PENDING_LEDGERS'
  | 'NO_HOLDERS'
  | 'ALL_HOLDERS_EXCLUDED'
  // Warnings only.
  | 'FACE_PRICE_EDITED'
  | 'NO_PAR_VALUE'
  | 'NO_MATURITY_DATE'
  | 'NOT_YET_MATURED'
  | 'HOLDERS_EXCLUDED';

export interface BondMaturityPendingLedger {
  ledgerId: string;
  userId: string;
  source: string;
  status: string;
  quantity: string;
}

/** Only the fields the code carries are present. */
export interface BondMaturityBlocker {
  code: BondMaturityBlockerCode;
  message?: string;
  count?: number;
  runId?: string;
  status?: string;
  orderIDs?: (string | number)[];
  userIds?: string[];
  symbols?: string[];
  statuses?: string[];
  ledgers?: BondMaturityPendingLedger[];
  facePrice?: string;
  bondParValue?: string;
  maturityDate?: string;
}

export interface BondMaturityRequest {
  /** The bond, e.g. `AMGB10000001~AMX`. */
  instrumentInternalId: string;
  /** Per bond unit, in the instrument's price currency. */
  facePrice: number | string;
  excludedUserIds?: string[];
}

export interface RunBondMaturityPayload extends BondMaturityRequest {
  /** Required. Without it the backend refuses and nothing happens. */
  confirm: true;
  note?: string;
  /** Defaults true. Turn off only when back office books the redemption in 1C itself. */
  syncTo1C?: boolean;
}

export interface BondMaturityTotals {
  /** Decimal strings. */
  quantity: string;
  cashAmount: string;
}

export interface BondMaturityPreview {
  instrument: {
    instrumentInternalId: string;
    tickerId: string;
    name?: string | null;
    isin?: string | null;
    status?: string;
    assetClassId?: string;
    bondParValue?: number | string | null;
    bondMaturityDate?: string | null;
  };
  facePrice: string;
  priceCurrency: string | null;
  /** Where the cash lands. Differs from priceCurrency only for minor units (GBX -> GBP). */
  cashCurrency: string | null;
  holders: { count: number; payable: number; excluded: number };
  totals: BondMaturityTotals;
  blockers: BondMaturityBlocker[];
  warnings: BondMaturityBlocker[];
  canRun: boolean;
}

export type BondMaturityHolderFilter = 'all' | 'payable' | 'excluded' | 'flagged';

export interface BondMaturityHoldersRequest extends BondMaturityRequest {
  page: number;
  itemsPerPage: number;
  keyword?: string;
  filter?: BondMaturityHolderFilter;
}

export interface BondMaturityHolder {
  userId: string;
  email: string | null;
  name: string | null;
  brokerageAccount: string | null;
  kycStatus: string | null;
  /** Decimal strings. */
  quantity: string;
  cashAmount: string;
  averageCostPrice: string;
  excluded: boolean;
  /** `kyc-not-approved` or `user-not-found`. Shown, never enforced. */
  flag: string | null;
}

export interface BondMaturityHoldersResponse {
  total: number;
  items: BondMaturityHolder[];
  /** Every id the filter and keyword matched, across pages. */
  matchingUserIds: string[];
  summary: { totalHolders: number; payable: number; excluded: number; flagged: number };
  totals: BondMaturityTotals;
  facePrice: string;
  priceCurrency: string | null;
  cashCurrency: string | null;
  blockers: BondMaturityBlocker[];
  warnings: BondMaturityBlocker[];
  canRun: boolean;
}

export interface BondMaturityEntry {
  userId: string;
  quantity: string;
  cashAmount: string;
  bondLedgerId: string | null;
  cashLedgerId: string | null;
  status: BondMaturityEntryStatus;
  reason: string | null;
  paidAt: string | null;
}

export interface BondMaturityRun {
  id: string;
  instrumentInternalId: string;
  isin?: string | null;
  priceCurrency: string;
  cashCurrency: string;
  facePrice: string;
  defaultFacePrice: string | null;
  maturityDate?: string | null;
  excludedUserIds: string[];
  status: BondMaturityStatus;
  adminId?: string;
  note?: string | null;
  syncTo1C: boolean;
  startedAt?: string;
  finishedAt?: string;
  instrumentDeactivated: boolean;
  totals: {
    holders: number;
    paid: number;
    excluded: number;
    skipped: number;
    failed: number;
    quantity: string;
    cashAmount: string;
  };
  error?: string | null;
  created?: string;
  /** Detail endpoint only. */
  entries?: BondMaturityEntry[];
}

export interface BondMaturityListParams {
  page: number;
  itemsPerPage: number;
  instrumentInternalId?: string;
  status?: string;
}

@Injectable({ providedIn: 'root' })
export class BondMaturityService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  /** Read-only. */
  preview(payload: BondMaturityRequest): Observable<BondMaturityPreview> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/bond-maturities/preview`,
      payload
    );
  }

  /** Read-only. Paged holder list for the checkbox table. */
  holders(
    payload: BondMaturityHoldersRequest
  ): Observable<BondMaturityHoldersResponse> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/bond-maturities/preview/holders`,
      payload
    );
  }

  /** Irreversible. Only call from a state the preview authorised. */
  run(payload: RunBondMaturityPayload): Observable<BondMaturityRun> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/bond-maturities`,
      payload
    );
  }

  list(
    params: BondMaturityListParams
  ): Observable<{ items: BondMaturityRun[]; total: number }> {
    const query: Record<string, string | number> = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.instrumentInternalId) {
      query['instrumentInternalId'] = params.instrumentInternalId;
    }
    if (params.status) query['status'] = params.status;
    return this.networkHelper.get(`${this.hostUrl}/admin/bond-maturities`, {
      ...query,
    });
  }

  /** The run record with its per-holder entries. */
  getById(id: string): Observable<BondMaturityRun> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/bond-maturities/${encodeURIComponent(id)}`
    );
  }
}
